import Sortable from 'sortablejs';
import ApplicationController from './application_controller';

// Layout canvas using pure Bootstrap rows/cols (no Gridstack). Each form control becomes a
// `.col-md-{w}` cell inside a single `.row` that auto-wraps when cumulative widths exceed 12.
// Cells are reorderable via SortableJS and resizable via -/+ buttons that step width by 1,
// clamped to the slug's declared minimum. On every change we re-serialize to the hidden
// textarea, walking the DOM in order and computing `x`/`y` from width accumulation so the
// saved `{grid:{cells:[...]}}` schema stays stable with Phase 1's renderer.
//
// Ordering: the editor's controls array (the Structure tab) is the single source of truth.
// The saved layout contributes widths only; render() always paints in structure order, and a
// drag here pushes the new order back to the Structure tab via `layout-reordered`. The two
// tabs can never disagree on order — a prerequisite for merging them into one tab.
export default class extends ApplicationController {
  static targets = ['canvas', 'hidden', 'emptyNotice'];

  static values = {
    layout: Object,
    minWidths: Object,
    defaultMinWidth: Number,
    slugNames: Object,
    semanticRoleLabels: Object,
  };

  connect() {
    this.boundOnTabShown = this.onTabShown.bind(this);
    this.boundOnEditorReady = this.onEditorReady.bind(this);
    this.boundOnSyncLayout = this.onSyncLayout.bind(this);
    this.boundOnControlsChanged = this.onControlsChanged.bind(this);
    $(document).on('shown.bs.tab', 'a[href="#custom-form-layout"]', this.boundOnTabShown);
    document.addEventListener('custom-form-editor:ready', this.boundOnEditorReady);
    document.addEventListener('custom-form-editor:sync-layout', this.boundOnSyncLayout);
    document.addEventListener('custom-form-editor:controls-changed', this.boundOnControlsChanged);
    this.seedHiddenFromValue();
    this.render();
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }
    $(document).off('shown.bs.tab', 'a[href="#custom-form-layout"]', this.boundOnTabShown);
    document.removeEventListener('custom-form-editor:ready', this.boundOnEditorReady);
    document.removeEventListener('custom-form-editor:sync-layout', this.boundOnSyncLayout);
    document.removeEventListener('custom-form-editor:controls-changed', this.boundOnControlsChanged);
  }

  onTabShown() {
    this.render();
  }

  onEditorReady() {
    this.render();
  }

  // Reconcile the layout against the current controls array on demand. Dispatched by the editor's
  // save path so a control added on the Structure tab without ever visiting the Layout tab still
  // makes it into the saved `grid.cells`. render() is synchronous and serialize() writes the hidden
  // textarea before this returns, so the caller can read the textarea immediately after.
  onSyncLayout() {
    this.render();
  }

  // Structure-tab mutations (add, clone, delete, move, drag, label edit) dispatch
  // `controls-changed` so the layout stays live without waiting for a save or tab switch.
  // Coalesced through a microtask: the template-load path fires one dispatch per control in a
  // single synchronous loop, and this collapses the burst into one render. The save path must
  // NOT use this — it relies on the synchronous `sync-layout` handler above.
  onControlsChanged() {
    if (this.renderQueued) return;
    this.renderQueued = true;
    Promise.resolve().then(() => {
      this.renderQueued = false;
      if (!this.element.isConnected) return;
      this.render();
    });
  }

  reset() {
    this.hiddenTarget.value = '{}';
    this.render();
  }

  toggleEmptyNotice(empty) {
    if (!this.hasEmptyNoticeTarget) return;
    this.emptyNoticeTarget.classList.toggle('hidden', !empty);
  }

  seedHiddenFromValue() {
    const current = this.hiddenTarget.value.trim();
    if (current && current !== '{}') return;
    this.hiddenTarget.value = JSON.stringify(this.layoutValue || {});
  }

  currentControls() {
    const raw = (window.customFormEditor && window.customFormEditor.controls) || [];
    return (Array.isArray(raw) ? raw : Object.values(raw)).filter(Boolean);
  }

  minWidthFor(control) {
    const map = this.minWidthsValue || {};
    return map[control.slug] || this.defaultMinWidthValue || 3;
  }

  readHidden() {
    try {
      const v = this.hiddenTarget.value.trim();
      return v ? JSON.parse(v) : {};
    } catch {
      return {};
    }
  }

  // Reconcile saved layout with the current controls array, then paint the canvas with
  // Bootstrap cells. The controls array (the Structure tab's order) is the single source of
  // truth for ordering — saved cells only contribute their widths, keyed by control_id.
  // Controls absent from the saved layout auto-seed as full-width at their structure position;
  // saved cells referencing controls that no longer exist fall away.
  render() {
    const controlsList = this.currentControls().filter((c) => c && c.id);
    this.toggleEmptyNotice(controlsList.length === 0);
    if (controlsList.length === 0) {
      if (this.sortable) {
        this.sortable.destroy();
        this.sortable = null;
      }
      this.canvasTarget.innerHTML = '';
      return;
    }

    const incoming = this.readHidden();
    const rawCells = toCellArray(incoming && incoming.grid && incoming.grid.cells);
    const cellsByControlId = {};
    rawCells.forEach((cell) => {
      if (cell && cell.control_id && !cellsByControlId[cell.control_id]) cellsByControlId[cell.control_id] = cell;
    });

    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }
    const canvas = this.canvasTarget;
    canvas.innerHTML = '';
    canvas.classList.add('row', 'layout-canvas-row');

    controlsList.forEach((control) => {
      const cell = cellsByControlId[control.id] || { w: 12 };
      const minW = this.minWidthFor(control);
      const w = clamp(Number(cell.w) || 12, minW, 12);
      canvas.appendChild(this.buildCell(control, w, minW));
    });

    this.sortable = new Sortable(canvas, {
      animation: 150,
      handle: '.layout-drag-handle',
      draggable: '.layout-cell',
      ghostClass: 'layout-cell-ghost',
      onEnd: () => {
        this.serialize();
        this.notifyReorder();
      },
    });

    this.serialize();
  }

  // After a drag on the layout canvas, hand the new order back to the Structure tab so the two
  // stay in lockstep (one shared order, in prep for unifying the tabs). The editor script
  // listens for this and reorders both its controls array and the structure canvas DOM.
  notifyReorder() {
    const ids = Array.from(this.canvasTarget.querySelectorAll('.layout-cell')).map((col) => col.dataset.controlId);
    document.dispatchEvent(new CustomEvent('custom-form-editor:layout-reordered', { detail: { ids } }));
  }

  buildCell(control, w, minW) {
    const col = document.createElement('div');
    col.className = `col-12 col-md-${w} layout-cell`;
    col.dataset.controlId = control.id;
    col.dataset.slug = control.slug;
    col.dataset.minW = String(minW);
    col.dataset.w = String(w);

    const label =
      (control.properties && control.properties.label_text && control.properties.label_text.value) || control.slug;
    const friendlyName = (this.slugNamesValue && this.slugNamesValue[control.slug]) || control.slug;
    // Field meaning (semantic_role): append its label to the type name ("Rich Text Area ~ Subjective
    // data"), and for standard controls flag them "semi-smart" so they get the pastel-blue cell —
    // a standard control with a meaning is more than plain standard, less than a true smart control.
    const role =
      (control.properties && control.properties.semantic_role && control.properties.semantic_role.value) || '';
    const roleLabel = (role && this.semanticRoleLabelsValue && this.semanticRoleLabelsValue[role]) || '';
    const typeName = roleLabel ? `${friendlyName} ~ ${roleLabel}` : friendlyName;
    const semiSmartClass = control.category === 'standard' && roleLabel ? ' layout-cell-semi-smart' : '';
    const categoryClass = (control.category ? ` layout-cell-${control.category}` : '') + semiSmartClass;
    const defaultMin = this.defaultMinWidthValue || 3;
    const minHint =
      minW > defaultMin
        ? `<span class="layout-width-min" title="${escapeHtml(control.slug)} cannot shrink below ${minW}/12">min ${minW}</span>`
        : '';
    col.innerHTML = `
      <div class="layout-cell-inner${categoryClass}">
        <div class="layout-cell-edit-view">
          <div class="layout-cell-toolbar">
            <i class="bi bi-grip-vertical layout-drag-handle" title="Drag to reorder"></i>
            <span class="layout-cell-slug" title="${escapeHtml(control.slug)}">${escapeHtml(typeName)}</span>
            ${minHint}
            <div class="layout-width-controls">
              <button type="button" class="btn btn-sm btn-light layout-width-down" data-action="layout-canvas#widthDown" title="Shrink"><i class="bi bi-dash-lg"></i></button>
              <span class="layout-width-value">${w}</span>
              <button type="button" class="btn btn-sm btn-light layout-width-up" data-action="layout-canvas#widthUp" title="Widen"><i class="bi bi-plus-lg"></i></button>
            </div>
          </div>
          <div class="layout-cell-label">
            <div class="layout-cell-label-text">${escapeHtml(label)}</div>
          </div>
        </div>
        <div class="layout-cell-resize-grip" data-action="mousedown->layout-canvas#startResize" title="Drag to resize"></div>
      </div>
    `;
    this.updateWidthButtonStates(col);
    return col;
  }

  // Drag-to-resize from the bottom-right corner grip. Shows a faded-yellow ghost tracking the
  // target width as the user drags, and only commits the new width (with serialize + layout
  // reflow) on mouseup. That way the row doesn't jitter through intermediate sizes as the user
  // sweeps across. Vertical drag is ignored — cell height is intrinsic to the control.
  startResize(event) {
    event.preventDefault();
    const col = event.currentTarget.closest('.layout-cell');
    if (!col) return;
    const rowEl = col.parentElement;
    if (!rowEl) return;
    const unitWidth = rowEl.getBoundingClientRect().width / 12;
    if (!unitWidth || !Number.isFinite(unitWidth)) return;
    const startX = event.clientX;
    const startW = Number(col.dataset.w) || 12;
    const minW = Number(col.dataset.minW) || 3;
    const colRect = col.getBoundingClientRect();

    const ghost = document.createElement('div');
    ghost.className = 'layout-cell-resize-ghost';
    ghost.style.top = `${colRect.top}px`;
    ghost.style.left = `${colRect.left}px`;
    ghost.style.height = `${colRect.height}px`;
    ghost.style.width = `${colRect.width}px`;
    const label = document.createElement('span');
    label.className = 'layout-cell-resize-ghost-label';
    label.textContent = `${startW} / 12`;
    ghost.appendChild(label);
    document.body.appendChild(ghost);

    let targetW = startW;

    const onMove = (e) => {
      const dw = Math.round((e.clientX - startX) / unitWidth);
      const nextW = clamp(startW + dw, minW, 12);
      targetW = nextW;
      ghost.style.width = `${nextW * unitWidth}px`;
      label.textContent = `${nextW} / 12`;
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.classList.remove('layout-cell-resizing');
      ghost.remove();
      if (targetW !== startW) {
        this.setCellWidth(col, targetW);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.classList.add('layout-cell-resizing');
  }

  widthUp(event) {
    const col = event.currentTarget.closest('.layout-cell');
    if (!col) return;
    this.setCellWidth(col, Number(col.dataset.w) + 1);
  }

  widthDown(event) {
    const col = event.currentTarget.closest('.layout-cell');
    if (!col) return;
    this.setCellWidth(col, Number(col.dataset.w) - 1);
  }

  setCellWidth(col, nextW) {
    const minW = Number(col.dataset.minW) || 3;
    const w = clamp(nextW, minW, 12);
    if (w === Number(col.dataset.w)) return;
    // Swap the col-md-{n} class
    Array.from(col.classList).forEach((cls) => {
      if (/^col-md-\d+$/.test(cls)) col.classList.remove(cls);
    });
    col.classList.add(`col-md-${w}`);
    col.dataset.w = String(w);
    const valueEl = col.querySelector('.layout-width-value');
    if (valueEl) valueEl.textContent = String(w);
    this.updateWidthButtonStates(col);
    this.serialize();
  }

  updateWidthButtonStates(col) {
    const w = Number(col.dataset.w);
    const minW = Number(col.dataset.minW) || 3;
    const slug = col.dataset.slug || '';
    const down = col.querySelector('.layout-width-down');
    const up = col.querySelector('.layout-width-up');
    if (down) {
      down.disabled = w <= minW;
      down.title = down.disabled ? `${slug} cannot shrink below ${minW}/12` : 'Shrink';
    }
    if (up) {
      up.disabled = w >= 12;
      up.title = up.disabled ? 'At maximum width (12/12)' : 'Widen';
    }
  }

  // Walk the DOM in order, computing x/y from width accumulation: when adding a cell would
  // exceed 12 columns in the current row, wrap to the next row. This matches Bootstrap's
  // flex-wrap visual behavior, so the saved schema reflects what the designer sees.
  serialize() {
    const cells = [];
    let x = 0;
    let y = 0;
    this.canvasTarget.querySelectorAll('.layout-cell').forEach((col) => {
      const w = Number(col.dataset.w) || 12;
      if (x + w > 12) {
        y += 1;
        x = 0;
      }
      cells.push({ control_id: col.dataset.controlId, x, y, w });
      x += w;
      if (x >= 12) {
        y += 1;
        x = 0;
      }
    });
    this.hiddenTarget.value = JSON.stringify({ grid: { columns: 12, cells } });
  }
}

function toCellArray(cells) {
  if (Array.isArray(cells)) return cells;
  if (cells && typeof cells === 'object') return Object.values(cells);
  return [];
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
